import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { circuitsService, type CircuitWithGeoJSON } from '../../api/circuitsService';

interface Station {
  reference: string; // Station ID within the event (e.g., "1", "2", "3")
  lat: number;
  lon: number;
  altitude: number;
  nearest_segment_direction: number;
}

interface CircuitPreviewProps {
  circuitId: string;
  geojsonId?: string | null; // Optional custom GeoJSON file ID (defaults to circuitId if not set)
  theme?: 'light' | 'dark';
  stations?: Station[];
  circuitData?: CircuitWithGeoJSON | null; // Optional pre-loaded circuit data
}

// GeoJSON types
interface GeoJSONGeometry {
  type: string;
  coordinates: number[] | number[][] | number[][][];
}

interface GeoJSONFeature {
  type: string;
  geometry: GeoJSONGeometry;
  properties?: {
    name?: string;
    marker?: string;
    [key: string]: unknown;
  };
}

interface GeoJSONFeatureCollection {
  type: string;
  features: GeoJSONFeature[];
}

interface SvgPathResult {
  path: string;
  viewBox: string;
  toSvgX: (lng: number) => number;
  toSvgY: (lat: number) => number;
}

/**
 * Converts GeoJSON coordinates to SVG path while preserving the circuit's
 * natural aspect ratio (applying latitude-based correction for longitude).
 */
const geojsonToSVGPath = (geojson: GeoJSONFeatureCollection): SvgPathResult | null => {
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    return null;
  }

  // Find the track feature (usually LineString)
  const trackFeature = geojson.features.find(
    (f) => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
  );

  if (!trackFeature) {
    return null;
  }

  // Get coordinates as array of [lng, lat] pairs
  const coordinates: number[][] = trackFeature.geometry.type === 'LineString'
    ? (trackFeature.geometry.coordinates as number[][])
    : (trackFeature.geometry.coordinates as number[][][])[0]; // Take first line if MultiLineString

  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  // Calculate geographic bounds
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  coordinates.forEach((coord) => {
    minLng = Math.min(minLng, coord[0]);
    minLat = Math.min(minLat, coord[1]);
    maxLng = Math.max(maxLng, coord[0]);
    maxLat = Math.max(maxLat, coord[1]);
  });

  // Apply latitude correction so longitude degrees are proportional to latitude degrees
  const midLat = (minLat + maxLat) / 2;
  const lngScale = Math.cos((midLat * Math.PI) / 180);

  // Calculate corrected dimensions
  const geoWidth = (maxLng - minLng) * lngScale;
  const geoHeight = maxLat - minLat;

  // Normalize to SVG coordinates preserving aspect ratio
  const maxDim = Math.max(geoWidth, geoHeight);
  if (maxDim === 0) return null;

  const svgWidth = (geoWidth / maxDim) * 100;
  const svgHeight = (geoHeight / maxDim) * 100;

  // Add padding (8% on each side)
  const pad = 0.08;
  const padX = svgWidth * pad;
  const padY = svgHeight * pad;
  const viewBox = `${-padX} ${-padY} ${svgWidth + padX * 2} ${svgHeight + padY * 2}`;

  // Projection helpers for converting geographic coords to SVG coords
  const toSvgX = (lng: number) => (lng - minLng) * lngScale / maxDim * 100;
  const toSvgY = (lat: number) => (maxLat - lat) / maxDim * 100;

  // Build SVG path with corrected proportions
  const pathData = coordinates.map((coord, i: number) => {
    const x = toSvgX(coord[0]);
    const y = toSvgY(coord[1]);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');

  return { path: pathData, viewBox, toSvgX, toSvgY };
};

export const CircuitPreview = ({ circuitId, geojsonId, theme = 'dark', stations = [], circuitData }: CircuitPreviewProps) => {
  const [circuit, setCircuit] = useState<CircuitWithGeoJSON | null>(circuitData || null);
  const [loading, setLoading] = useState(!circuitData);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If circuit data is provided, use it immediately
    if (circuitData) {
      setCircuit(circuitData);
      setLoading(false);
      setError(false);
      return;
    }

    // Otherwise, load it
    const loadCircuit = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await circuitsService.getCircuitById(circuitId, geojsonId);
        setCircuit(data);
      } catch (err) {
        console.error(`Failed to load circuit preview for ${geojsonId || circuitId}:`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadCircuit();
  }, [circuitId, geojsonId, circuitData]);

  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (error || !circuit?.geojson) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme === 'dark' ? '#666' : '#999',
          fontSize: '10px',
        }}
      >
        N/A
      </div>
    );
  }

  const svgData = geojsonToSVGPath(circuit.geojson);

  if (!svgData) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme === 'dark' ? '#666' : '#999',
          fontSize: '10px',
        }}
      >
        N/A
      </div>
    );
  }

  // Find start/finish markers in the GeoJSON
  const startFinishFeature = circuit.geojson.features.find(
    (f) => f.geometry.type === 'Point' && 
    (f.properties?.name === 'Start/Finish' || f.properties?.marker === 'start-finish')
  );

  const strokeColor = '#0E447D';
  const { toSvgX, toSvgY } = svgData;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        viewBox={svgData.viewBox}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Circuit track */}
        <path
          d={svgData.path}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Start/Finish marker (optional, if we have the data) */}
        {startFinishFeature && startFinishFeature.geometry.type === 'Point' && (() => {
          const pointCoords = startFinishFeature.geometry.coordinates as unknown as number[];
          const cx = toSvgX(pointCoords[0]);
          const cy = toSvgY(pointCoords[1]);
          return (
            <circle
              cx={cx}
              cy={cy}
              r="3"
              fill="#ff4444"
              stroke="#fff"
              strokeWidth="0.5"
            />
          );
        })()}
        
        {/* Weather stations */}
        {stations.map((station, index) => {
          const cx = toSvgX(station.lon);
          const cy = toSvgY(station.lat);
          
          return (
            <g key={station.reference || index}>
              <circle
                cx={cx}
                cy={cy}
                r="4"
                fill="#FFA500"
                stroke="#fff"
                strokeWidth="0.8"
                opacity="0.95"
              >
                <title>{`Station ${station.reference}\nAltitude: ${station.altitude}m`}</title>
              </circle>
              
              <text
                x={cx}
                y={cy}
                fontSize="4.5"
                fill="#000"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                pointerEvents="none"
                style={{ 
                  userSelect: 'none',
                  textShadow: '0 0 2px #fff, 0 0 2px #fff'
                }}
              >
                {station.reference}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

